import { OverlayModule } from '@angular/cdk/overlay';
import { provideHttpClient, withFetch, withInterceptors } from '@angular/common/http';
import { ApplicationConfig, LOCALE_ID, importProvidersFrom, isDevMode, provideZoneChangeDetection } from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { TitleStrategy, provideRouter } from '@angular/router';
import { provideEffects } from '@ngrx/effects';
import { provideRouterStore, routerReducer } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import * as echarts from 'echarts';
import { KeycloakAngularModule } from 'keycloak-angular';
import { provideEchartsCore } from 'ngx-echarts';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { NGX_MONACO_EDITOR_CONFIG, NgxMonacoEditorConfig } from 'ngx-monaco-editor-v2';

import { keycloakInitializer } from '@core/initializers/keycloak';
import { localeDataInitializer } from '@core/initializers/locale-data';
import { translationsPreloadInitializer } from '@core/initializers/translations-preload';
import { websocketInitializer } from '@core/initializers/websocket';
import { authInterceptor } from '@core/interceptors/auth.interceptor';
import { luxonAdapterProviders } from '@core/luxon-adapter';
import { TitleService } from '@core/services/title.service';
import { AiAssistantEffects } from '@core/stores/ai-assistant/ai-assistant.effects';
import { aiAssistantFeatureKey, aiAssistantReducer } from '@core/stores/ai-assistant/ai-assistant.reducer';
import { AuthEffects } from '@core/stores/auth/auth.effects';
import { authFeatureKey, authReducer } from '@core/stores/auth/auth.reducer';
import { BreakpointEffects } from '@core/stores/breakpoint/breakpoint.effects';
import { breakpointFeatureKey, breakpointReducer } from '@core/stores/breakpoint/breakpoint.reducer';
import { TenantsEffects } from '@core/stores/tenants/tenants.effects';
import { tenantsFeatureKey, tenantsReducer } from '@core/stores/tenants/tenants.reducer';
import { WebsocketEffects } from '@core/stores/websocket/websocket.effects';
import { websocketFeatureKey, websocketReducer } from '@core/stores/websocket/websocket.reducer';

import { DateInputModule } from '@shared/modules/date-input';
import { DIALOG_DEFAULT_OPTIONS, DialogConfig, DialogModule } from '@shared/modules/dialog';
import { LOCALE_ID_VALUE } from '@shared/utils/locales';

import { routes } from './app.routes';
import { providePostHogErrorHandler } from './core/analytics/posthog-error-handler';
import { iconProvider, provideTranslate } from './core/providers';

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    providePostHogErrorHandler(),
    provideRouter(routes),
    provideStore({
      [websocketFeatureKey]: websocketReducer,
      [authFeatureKey]: authReducer,
      [breakpointFeatureKey]: breakpointReducer,
      [aiAssistantFeatureKey]: aiAssistantReducer,
      [tenantsFeatureKey]: tenantsReducer,
      router: routerReducer,
    }),
    provideEffects(WebsocketEffects, BreakpointEffects, AuthEffects, AiAssistantEffects, TenantsEffects),
    provideRouterStore(),
    provideHttpClient(withFetch(), withInterceptors([authInterceptor])),
    provideTranslate(),
    iconProvider,
    translationsPreloadInitializer,
    localeDataInitializer,
    websocketInitializer,
    ...luxonAdapterProviders,
    provideAnimations(),
    provideStoreDevtools({ maxAge: 25, logOnly: !isDevMode() }),
    importProvidersFrom(KeycloakAngularModule),
    keycloakInitializer,
    importProvidersFrom(OverlayModule),
    provideEnvironmentNgxMask(),
    { provide: LOCALE_ID, useValue: LOCALE_ID_VALUE },
    {
      provide: DIALOG_DEFAULT_OPTIONS,
      useValue: {
        width: '560px',
        maxWidth: 'calc(100%) - 16px',
        hasBackdrop: true,
      } satisfies DialogConfig,
    },
    {
      provide: NGX_MONACO_EDITOR_CONFIG,
      useValue: {} satisfies NgxMonacoEditorConfig,
    },
    provideEchartsCore({ echarts }),
    importProvidersFrom(DateInputModule),
    { provide: TitleStrategy, useClass: TitleService },
    importProvidersFrom(DialogModule),
  ],
};
